import jsPDF, { GState } from 'jspdf';
import type { Product } from '../types/product';
import type { LangStrings } from '../i18n/en';
import { formatDose } from './formatDose';

const LOGO_PNG_URL = '/VIV_Logo_main.png';
const WATERMARK_URL = '/VIV_Watermark_V.svg';
const WATERMARK_ASPECT = 7224.59 / 4843.86;

const VIVATIS_ORANGE: RGB = [244, 160, 97];
const ANTHRACITE: RGB    = [45, 51, 57];
const LABEL_GRAY: RGB    = [157, 163, 169];
const META_GRAY: RGB     = [138, 144, 153];
const LINK_BLUE: RGB     = [59, 130, 246];
const WHITE: RGB         = [255, 255, 255];
const FACTS_BG: RGB      = [248, 249, 250];
const DIVIDER_COLOR: RGB = [232, 234, 237];
const PITCH_BG: RGB      = [255, 248, 240];
const CONF_GRAY: RGB     = [176, 181, 186];
const DESC_COLOR: RGB    = [61, 67, 73];
const STUDY_COLOR: RGB   = [90, 96, 105];

type RGB = [number, number, number];

type PdfStrings = LangStrings['pdf'];

/** Download a jsPDF document as a Blob via object URL. */
function downloadBlob(doc: jsPDF, fileName: string): void {
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let logoDataUrl: string | null = null;

async function loadLogo(): Promise<string> {
  if (logoDataUrl) return logoDataUrl;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      logoDataUrl = canvas.toDataURL('image/png');
      resolve(logoDataUrl);
    };
    img.onerror = () => resolve('');
    img.src = LOGO_PNG_URL;
  });
}

function addLogo(doc: jsPDF, dataUrl: string, x: number, y: number, width: number) {
  if (!dataUrl) return;
  const height = width / (800 / 189);
  doc.addImage(dataUrl, 'PNG', x, y, width, height, 'vivatis-logo', 'FAST');
}

let watermarkDataUrl: string | null = null;

async function loadWatermark(): Promise<string> {
  if (watermarkDataUrl) return watermarkDataUrl;
  const response = await fetch(WATERMARK_URL);
  const svgText = await response.text();
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // 7% opacity decoration: low resolution + JPEG keeps it tiny without visible loss.
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = Math.round(800 / WATERMARK_ASPECT);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        watermarkDataUrl = canvas.toDataURL('image/jpeg', 0.6);
      } else {
        watermarkDataUrl = '';
      }
      URL.revokeObjectURL(objectUrl);
      resolve(watermarkDataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve('');
    };
    img.src = objectUrl;
  });
}

interface ImageEntry {
  dataUrl: string;
  width: number;
  height: number;
  format: 'JPEG' | 'PNG';
  alias: string;
}

const imageCache = new Map<string, ImageEntry | null>();

function toSameOriginUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

async function loadImageFromUrl(url: string | null | undefined): Promise<ImageEntry | null> {
  if (!url) return null;
  const fetchUrl = toSameOriginUrl(url);
  if (imageCache.has(url)) return imageCache.get(url) ?? null;
  let entry: ImageEntry | null = null;
  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error('fetch failed');
    const blob = await response.blob();
    const mime = blob.type || '';
    const isJpeg = mime === 'image/jpeg' || mime === 'image/jpg';
    const isPng = mime === 'image/png';
    const objectUrl = URL.createObjectURL(blob);
    entry = await new Promise<ImageEntry | null>((resolve) => {
      const img = new Image();
      img.onload = async () => {
        try {
          // Preserve original bytes for JPEG/PNG to avoid canvas re-encode bloat.
          if (isJpeg || isPng) {
            const dataUrl = await new Promise<string>((res, rej) => {
              const r = new FileReader();
              r.onloadend = () => res(r.result as string);
              r.onerror = () => rej(new Error('FileReader failed'));
              r.readAsDataURL(blob);
            });
            resolve({
              dataUrl,
              width: img.naturalWidth,
              height: img.naturalHeight,
              format: isJpeg ? 'JPEG' : 'PNG',
              alias: `img:${url}`,
            });
            return;
          }
          // Fallback (webp, gif, svg, unknown): rasterize to JPEG for size.
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          // White background prevents black where transparent pixels would sit when encoding to JPEG.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          resolve({
            dataUrl: canvas.toDataURL('image/jpeg', 0.85),
            width: img.naturalWidth,
            height: img.naturalHeight,
            format: 'JPEG',
            alias: `img:${url}`,
          });
        } catch {
          resolve(null);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  } catch {
    entry = null;
  }
  imageCache.set(url, entry);
  return entry;
}

async function loadProductLogos(products: Product[]): Promise<Map<string, ImageEntry>> {
  const urls = new Set<string>();
  for (const product of products) {
    if (product.logoUrl) urls.add(product.logoUrl);
    if (product.partner?.logoUrl) urls.add(product.partner.logoUrl);
  }
  const entries = await Promise.all(
    [...urls].map(async (url) => [url, await loadImageFromUrl(url)] as const),
  );
  const map = new Map<string, ImageEntry>();
  for (const [url, entry] of entries) {
    if (entry) map.set(url, entry);
  }
  return map;
}

function drawFittedImage(
  doc: jsPDF,
  entry: ImageEntry,
  x: number,
  y: number,
  maxWidth: number,
  maxHeight: number,
  align: 'left' | 'right' = 'left',
): { width: number; height: number } {
  const aspect = entry.width / entry.height;
  let w = maxWidth;
  let h = w / aspect;
  if (h > maxHeight) {
    h = maxHeight;
    w = h * aspect;
  }
  const drawX = align === 'right' ? x - w : x;
  doc.addImage(entry.dataUrl, entry.format, drawX, y, w, h, entry.alias, 'FAST');
  return { width: w, height: h };
}

function drawWatermark(doc: jsPDF, dataUrl: string, pageW: number, pageH: number) {
  if (!dataUrl) return;
  const wmWidth = pageW * 0.6;
  const wmHeight = wmWidth / WATERMARK_ASPECT;
  const x = (pageW - wmWidth) / 2;
  const y = (pageH - wmHeight) / 2;
  doc.saveGraphicsState();
  doc.setGState(new GState({ opacity: 0.07 }));
  doc.addImage(dataUrl, 'JPEG', x, y, wmWidth, wmHeight, 'watermark', 'FAST');
  doc.restoreGraphicsState();
}

function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}

function getCategoryColor(product: Product): RGB {
  if (product.category?.color) return hexToRgb(product.category.color);
  return VIVATIS_ORANGE;
}

function sanitize(text: string): string {
  return text
    .replace(/\u2265/g, '>=')
    .replace(/\u2264/g, '<=')
    .replace(/\u00AE/g, '(R)')
    .replace(/\u00A9/g, '(c)')
    .replace(/\u2013/g, '-')
    .replace(/\u2014/g, '--')
    .replace(/\u2018/g, "'")
    .replace(/\u2019/g, "'")
    .replace(/\u201C/g, '"')
    .replace(/\u201D/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00B5/g, 'u')
    .replace(/\u00B0/g, 'deg')
    .replace(/[\u0100-\u2121\u2123-\uFFFF]/g, '');
}

function splitText(doc: jsPDF, text: string, maxWidth: number): string[] {
  return doc.splitTextToSize(sanitize(text), maxWidth);
}

function splitBulletTextStyled(doc: jsPDF, text: string, maxWidth: number): PdfLine[] {
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return htmlToPdfLines(doc, text, maxWidth);
  }
  return splitBulletText(doc, text, maxWidth).map((t) => ({ text: t }));
}

function splitBulletText(doc: jsPDF, text: string, maxWidth: number): string[] {
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return htmlToPdfLines(doc, text, maxWidth).map((l) => l.text);
  }

  const hashIdx = text.indexOf('#');
  if (hashIdx !== -1) {
    const intro = text.slice(0, hashIdx).trim();
    const items = text.slice(hashIdx + 1).split(';').map((i) => i.trim()).filter(Boolean);
    if (items.length === 0) return splitText(doc, text, maxWidth);
    const result: string[] = [];
    if (intro) result.push(...splitText(doc, intro, maxWidth));
    for (const item of items) {
      result.push(...doc.splitTextToSize(sanitize(`- ${item}`), maxWidth));
    }
    return result;
  }

  const rawLines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (rawLines.length <= 1) return splitText(doc, text, maxWidth);
  const result: string[] = [];
  for (const line of rawLines) {
    result.push(...doc.splitTextToSize(sanitize(`- ${line}`), maxWidth));
  }
  return result;
}

interface PdfLine {
  text: string;
  style?: 'bold' | 'italic' | 'bolditalic';
  heading?: boolean;
  indent?: number;
}

function htmlToPdfLines(doc: jsPDF, html: string, maxWidth: number): PdfLine[] {
  const parser = new DOMParser();
  const dom = parser.parseFromString(html, 'text/html');
  const result: PdfLine[] = [];

  function getInlineStyle(li: Element): PdfLine['style'] {
    for (const child of Array.from(li.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        const tag = el.tagName.toLowerCase();
        if (tag === 'strong' || tag === 'b') return 'bold';
        if (tag === 'em' || tag === 'i') return 'italic';
      }
    }
    return undefined;
  }

  function splitByBr(el: Element): string[] {
    const parts: string[] = [];
    let current = '';

    function walk(node: Node): void {
      if (node.nodeType === Node.TEXT_NODE) {
        current += node.textContent ?? '';
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = (node as Element).tagName.toLowerCase();
      if (tag === 'br') {
        parts.push(current);
        current = '';
        return;
      }
      for (const child of Array.from(node.childNodes)) {
        walk(child);
      }
    }

    walk(el);
    parts.push(current);
    return parts;
  }

  function walkNodes(parent: Node): void {
    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent ?? '').trim();
        if (text) {
          result.push(...splitText(doc, text, maxWidth).map((t) => ({ text: t })));
        }
        continue;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();

      if (tag === 'h2' || tag === 'h3') {
        const text = (el.textContent ?? '').trim();
        if (text) {
          for (const line of doc.splitTextToSize(sanitize(text), maxWidth)) {
            result.push({ text: line, style: 'bold', heading: true });
          }
        }
      } else if (tag === 'p') {
        const style = getInlineStyle(el);
        const segments = splitByBr(el);
        for (const seg of segments) {
          if (seg.trim()) {
            for (const line of splitText(doc, seg.trim(), maxWidth)) {
              result.push({ text: line, style });
            }
          } else {
            result.push({ text: ' ' });
          }
        }
      } else if (tag === 'ul' || tag === 'ol') {
        walkList(el, 0);
      } else {
        walkNodes(node);
      }
    }
  }

  function walkList(listEl: Element, depth: number): void {
    const prefix = depth === 0 ? '- ' : '    - ';
    const indentLevel = depth;

    for (const child of Array.from(listEl.children)) {
      if (child.tagName.toLowerCase() !== 'li') continue;

      const directText: string[] = [];
      const style = getInlineStyle(child);

      for (const liChild of Array.from(child.childNodes)) {
        if (liChild.nodeType === Node.TEXT_NODE) {
          const t = (liChild.textContent ?? '').trim();
          if (t) directText.push(t);
        } else if (liChild.nodeType === Node.ELEMENT_NODE) {
          const liEl = liChild as Element;
          const liTag = liEl.tagName.toLowerCase();
          if (liTag === 'ul' || liTag === 'ol') {
            if (directText.length > 0) {
              for (const line of doc.splitTextToSize(sanitize(`${prefix}${directText.join(' ')}`), maxWidth)) {
                result.push({ text: line, style, indent: indentLevel });
              }
              directText.length = 0;
            }
            walkList(liEl, depth + 1);
          } else {
            const t = (liEl.textContent ?? '').trim();
            if (t) directText.push(t);
          }
        }
      }

      if (directText.length > 0) {
        for (const line of doc.splitTextToSize(sanitize(`${prefix}${directText.join(' ')}`), maxWidth)) {
          result.push({ text: line, style, indent: indentLevel });
        }
      }
    }
  }

  walkNodes(dom.body);
  return result;
}

function renderPdfLines(
  doc: jsPDF,
  lines: PdfLine[],
  x: number,
  startY: number,
  lineH: number,
  baseSize: number,
  baseColor: [number, number, number],
  contentBottom: number,
  beginContinuationPage: () => number,
): number {
  let y = startY;

  for (const line of lines) {
    if (y + lineH > contentBottom) {
      y = beginContinuationPage();
    }

    const fontStyle = line.style === 'bold' || line.style === 'bolditalic'
      ? 'bold'
      : line.style === 'italic'
        ? 'italic'
        : 'normal';

    if (line.heading) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(baseSize + 1);
    } else {
      doc.setFont('helvetica', fontStyle);
      doc.setFontSize(baseSize);
    }
    doc.setTextColor(...baseColor);

    const indentX = x + (line.indent ?? 0) * 4;
    doc.text(line.text, indentX, y);
    y += lineH;
  }

  return y;
}

function formatDoseForPdf(product: Product): string {
  if (!product.doseFrom) return '-';
  return formatDose(product);
}

/** Section heading: orange accent | TITLE ———————— */
function drawSectionHeading(doc: jsPDF, title: string, y: number, margin: number, pageW: number): number {
  doc.setFillColor(...VIVATIS_ORANGE);
  doc.rect(margin, y, 2, 5, 'F');

  const upper = title.toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...ANTHRACITE);
  doc.text(upper, margin + 5, y + 4);

  const textW = doc.getTextWidth(upper);
  doc.setDrawColor(224, 224, 224);
  doc.setLineWidth(0.3);
  doc.line(margin + 7 + textW, y + 2.5, pageW - margin, y + 2.5);

  return y + 10;
}

/** Badge pills */
function drawBadges(
  doc: jsPDF, items: string[], y: number, margin: number, contentW: number,
  fill: RGB, textCol: RGB,
): number {
  let curX = margin;
  let curY = y;
  const h = 4.5;
  const pad = 2.5;
  const gap = 1.5;
  const maxX = margin + contentW;

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');

  for (const item of items) {
    const clean = sanitize(item);
    const tw = doc.getTextWidth(clean);
    const bw = tw + pad * 2;
    if (curX + bw > maxX) { curX = margin; curY += h + gap; }
    doc.setFillColor(...fill);
    doc.roundedRect(curX, curY, bw, h, 1.5, 1.5, 'F');
    doc.setTextColor(...textCol);
    doc.text(clean, curX + pad, curY + 3.2);
    curX += bw + gap;
  }

  return curY + h + 5;
}

/** Renders a labeled external link ("Label: url") and returns the next y position */
function renderLabeledLink(doc: jsPDF, label: string, url: string, margin: number, y: number): number {
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...META_GRAY);
  const labelText = `${sanitize(label)}: `;
  doc.text(labelText, margin, y);
  const labelWidth = doc.getTextWidth(labelText);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LINK_BLUE);
  doc.textWithLink(sanitize(url), margin + labelWidth, y, { url });
  return y + 4;
}

// ---------------------------------------------------------------------------
// Page renderers
// ---------------------------------------------------------------------------

function generateProductPage(doc: jsPDF, product: Product, isFirst: boolean, logo: string, watermark: string, logoMap: Map<string, ImageEntry>, p: PdfStrings, companyName = 'VIVATIS PHARMA GmbH', companyWebsite = 'www.vivatis.de') {
  if (!isFirst) doc.addPage();

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentW = pageW - margin * 2;
  const catColor = getCategoryColor(product);

  drawWatermark(doc, watermark, pageW, pageH);

  // --- Top bar (3.5mm) ---
  doc.setFillColor(...VIVATIS_ORANGE);
  doc.rect(0, 0, pageW, 3.5, 'F');

  // --- Left accent strip (45mm, from top bar) ---
  doc.setFillColor(...catColor);
  doc.rect(0, 3.5, 3, 45, 'F');

  // --- Header: vivatis logo | subtitle | product logo | date ---
  let y = 16;
  const logoH = 7;
  const logoW = logoH * (800 / 189);
  addLogo(doc, logo, margin, y, logoW);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...LABEL_GRAY);
  doc.text(p.productDataSheet, margin + logoW + 2.5, y + 4.5);

  const productLogo = product.logoUrl ? logoMap.get(product.logoUrl) : undefined;
  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const productLogoMaxH = 12;
  if (productLogo) {
    drawFittedImage(doc, productLogo, pageW - margin, y - 2, 45, productLogoMaxH, 'right');
    doc.setFontSize(6.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(dateStr, pageW - margin, y + productLogoMaxH + 2, { align: 'right' });
    y += productLogoMaxH + 4;
  } else {
    doc.setFontSize(6.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(dateStr, pageW - margin, y + 4.5, { align: 'right' });
    y += logoH + 2.5;
  }

  // --- Divider ---
  doc.setDrawColor(...DIVIDER_COLOR);
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // --- Product name ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(26, 31, 36);
  const nameFreeW = product.category ? contentW - 35 : contentW;
  const nameLines = splitText(doc, product.name, nameFreeW);
  doc.text(nameLines, margin, y);

  // NEW / NEW STUDY inline badges
  let badgeX = margin + doc.getTextWidth(nameLines[0]) + 3;
  if (product.isNew) {
    doc.setFillColor(34, 197, 94);
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    const txt = p.badgeNew;
    const w = doc.getTextWidth(txt) + 4;
    doc.roundedRect(badgeX, y - 4, w, 4.5, 1, 1, 'F');
    doc.setTextColor(...WHITE);
    doc.text(txt, badgeX + 2, y - 1);
    badgeX += w + 2;
  }
  if (product.hasNewStudy) {
    doc.setFillColor(...LINK_BLUE);
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    const txt = p.badgeNewStudy;
    const w = doc.getTextWidth(txt) + 4;
    doc.roundedRect(badgeX, y - 4, w, 4.5, 1, 1, 'F');
    doc.setTextColor(...WHITE);
    doc.text(txt, badgeX + 2, y - 1);
  }

  // --- Category badge (right-aligned, vertically centered with first name line) ---
  if (product.category?.name) {
    doc.setFillColor(...catColor);
    const catTxt = sanitize(product.category.name).toUpperCase();
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    const cw = doc.getTextWidth(catTxt) + 7;
    const badgeY = y - 4;
    doc.roundedRect(pageW - margin - cw, badgeY, cw, 5, 1.5, 1.5, 'F');
    doc.setTextColor(...WHITE);
    doc.text(catTxt, pageW - margin - cw + 3.5, badgeY + 3.5);
  }

  y += nameLines.length * 6 + 1.5;

  // --- Meta line ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...META_GRAY);
  const metaParts = [
    product.partner?.name,
    product.articleNumber,
    product.origin?.name ? `${p.origin}: ${product.origin.name}` : null,
  ].filter(Boolean);
  doc.text(sanitize(metaParts.join('  \xB7  ')), margin, y);
  y += 5;

  // --- Product website link ---
  if (product.websiteUrl) {
    y = renderLabeledLink(doc, p.website, product.websiteUrl, margin, y);
  }

  // --- Multimedia link ---
  if (product.multimediaUrl) {
    y = renderLabeledLink(doc, p.multimedia, product.multimediaUrl, margin, y);
  }

  // --- Pitch block ---
  if (product.shortDescription) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    const pitchLines = splitBulletTextStyled(doc, product.shortDescription, contentW - 10);
    const lineH = 4.2;
    const pitchPadding = 3;
    const pitchH = pitchLines.length * lineH + pitchPadding * 2;

    doc.setFillColor(...PITCH_BG);
    doc.rect(margin, y, contentW, pitchH, 'F');
    doc.setFillColor(...VIVATIS_ORANGE);
    doc.rect(margin, y, 2, pitchH, 'F');

    let pitchY = y + pitchPadding + lineH;
    for (const line of pitchLines) {
      const fontStyle = line.style === 'bold' ? 'bold' : line.style === 'italic' ? 'italic' : 'italic';
      doc.setFont('helvetica', line.heading ? 'bold' : fontStyle);
      doc.setFontSize(line.heading ? 8.5 : 7.5);
      doc.setTextColor(...ANTHRACITE);
      doc.text(line.text, margin + 5 + (line.indent ?? 0) * 4, pitchY);
      pitchY += lineH;
    }
    y += pitchH + 5;
  }

  // --- Key Facts (gray container like backend .facts-table) ---
  y = drawSectionHeading(doc, p.keyFacts, y, margin, pageW);

  // Collect short facts into a flat list (3-column grid). Long fields like
  // bioactive ingredients get their own full-width row so they aren't cut to
  // the first visual line.
  const allFacts: [string, string][] = [
    [p.dailyDosage, formatDoseForPdf(product)],
    [p.appearance, product.appearance?.name ?? '-'],
  ];
  if (product.clinicalStudies) allFacts.push([p.clinicalStudies, String(product.clinicalStudies)]);
  if (product.novelFoodStatus) allFacts.push([p.novelFoodStatus, product.novelFoodStatus]);
  if (product.shelfLifeMonths) allFacts.push([p.shelfLife, p.shelfLifeValue.replace(':months', String(product.shelfLifeMonths))]);
  if (product.moq) allFacts.push([p.moq, product.moq]);

  const cols = 3;
  const colW = (contentW - 6) / cols;
  const factsStartY = y;
  const rowH = 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const bioactiveLines = product.bioactiveIngredients
    ? splitBulletTextStyled(doc, product.bioactiveIngredients, contentW - 6)
    : [];
  const bioactiveH = bioactiveLines.length > 0 ? 5 + bioactiveLines.length * 4.2 + 2 : 0;

  const totalRows = Math.ceil(allFacts.length / cols);
  const factsH = totalRows * rowH + 4 + bioactiveH;

  // Gray background
  doc.setFillColor(...FACTS_BG);
  doc.roundedRect(margin, factsStartY - 1, contentW, factsH, 1.5, 1.5, 'F');

  // Draw short facts in grid
  for (let i = 0; i < allFacts.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const fx = margin + 3 + col * colW;
    const fy = factsStartY + 2 + row * rowH;

    const [label, value] = allFacts[i];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(label.toUpperCase(), fx, fy);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...ANTHRACITE);
    const vl = splitText(doc, value, colW - 6);
    doc.text(vl[0], fx, fy + 3.5);
  }

  // Draw bioactive ingredients full-width inside the facts box
  if (bioactiveLines.length > 0) {
    const bioY = factsStartY + 2 + totalRows * rowH + 1;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(p.bioactiveIngredient.toUpperCase(), margin + 3, bioY);

    let bioLineY = bioY + 3.5;
    const bioLineH = 4.2;
    for (const line of bioactiveLines) {
      const fontStyle = line.style === 'bold' ? 'bold' : line.style === 'italic' ? 'italic' : 'normal';
      doc.setFont('helvetica', line.heading ? 'bold' : fontStyle);
      doc.setFontSize(line.heading ? 8.5 : 8);
      doc.setTextColor(...ANTHRACITE);
      doc.text(line.text, margin + 3 + (line.indent ?? 0) * 4, bioLineY);
      bioLineY += bioLineH;
    }
  }

  y = factsStartY + factsH + 5;

  // --- Indications ---
  if (product.indications.length > 0) {
    y = drawSectionHeading(doc, p.indications, y, margin, pageW);
    y = drawBadges(doc, product.indications.map((i) => i.name), y, margin, contentW, [230, 244, 234], [26, 116, 49]);
  }

  // --- Possible Applications ---
  if (product.applications.length > 0) {
    y = drawSectionHeading(doc, p.possibleApplications, y, margin, pageW);
    y = drawBadges(doc, product.applications.map((a) => a.name), y, margin, contentW, [227, 238, 250], [26, 80, 145]);
  }

  // --- Marketing Claims ---
  if (product.marketingClaims.length > 0) {
    y = drawSectionHeading(doc, p.marketingClaims, y, margin, pageW);
    y = drawBadges(doc, product.marketingClaims.map((c) => c.name), y, margin, contentW, [254, 243, 226], [180, 83, 9]);
  }

  // Footer + continuation helpers (closures capture layout constants)
  const drawFooter = (): void => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(...CONF_GRAY);
    const confText = sanitize(p.confidential).toUpperCase();
    doc.text(confText, (pageW - doc.getTextWidth(confText)) / 2, pageH - 10);

    doc.setFillColor(...VIVATIS_ORANGE);
    doc.rect(0, pageH - 7, pageW, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(...WHITE);
    doc.text(companyName, margin, pageH - 2.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...WHITE);
    doc.text(companyWebsite, pageW - margin, pageH - 2.5, { align: 'right' });
  };

  const beginContinuationPage = (): number => {
    drawFooter();
    doc.addPage();

    // Compact chrome on continuation pages (top bar, short accent strip, logo + title)
    doc.setFillColor(...VIVATIS_ORANGE);
    doc.rect(0, 0, pageW, 3.5, 'F');
    doc.setFillColor(...catColor);
    doc.rect(0, 3.5, 3, 20, 'F');
    addLogo(doc, logo, margin, 10, logoW);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...ANTHRACITE);
    const titleLines = splitText(doc, sanitize(product.name), contentW - logoW - 30);
    doc.text(titleLines[0], margin + logoW + 5, 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(dateStr, pageW - margin, 14, { align: 'right' });

    return 26;
  };

  const contentBottom = pageH - 20; // leave room for confidential + footer bar
  // Page top for continuation pages (used to check "does the whole block fit
  // on a fresh page?").
  const continuationTopY = 26;
  const freshPageCapacity = contentBottom - continuationTopY;

  // --- Description ---
  if (product.pitch) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...DESC_COLOR);
    const descLines = splitBulletTextStyled(doc, product.pitch, contentW - 4);
    const headingH = 10;
    const lineH = 4;
    const blockH = headingH + descLines.length * lineH + 2;

    const fitsHere = blockH <= contentBottom - y;
    const fitsOnFreshPage = blockH <= freshPageCapacity;

    if (!fitsHere && fitsOnFreshPage) {
      y = beginContinuationPage();
    }

    y = drawSectionHeading(doc, p.description, y, margin, pageW);
    y += 1;

    y = renderPdfLines(doc, descLines, margin + 2, y, lineH, 7.5, DESC_COLOR, contentBottom, beginContinuationPage);
    y += 2;
  }

  // --- Link Studies ---
  if (product.studies) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...STUDY_COLOR);
    const studyLines = splitBulletTextStyled(doc, product.studies, contentW - 4);
    const headingH = 10;
    const lineH = 3.8;
    const blockH = headingH + studyLines.length * lineH + 4;

    const fitsHere = blockH <= contentBottom - y;
    const fitsOnFreshPage = blockH <= freshPageCapacity;

    if (!fitsHere && fitsOnFreshPage) {
      y = beginContinuationPage();
    }

    y = drawSectionHeading(doc, p.linkStudies, y, margin, pageW);
    y += 1;

    y = renderPdfLines(doc, studyLines, margin + 2, y, lineH, 7, STUDY_COLOR, contentBottom, beginContinuationPage);
    y += 4;
  }

  // Footer on the final page
  drawFooter();
}

function addPageNumbers(doc: jsPDF): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();

  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const text = i + ' / ' + total;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...WHITE);
    doc.text(text, (pageW - doc.getTextWidth(text)) / 2, pageH - 2.5);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------


/**
 * TEMP(VIVATISPDB-261): Product data on the backend is currently only stored
 * in English, so a localized PDF would mix translated UI chrome with English
 * content. Until the master data is fully localized, force every PDF export
 * to render with the English labels regardless of the caller's current UI
 * language. The `strings` parameter is kept for API compatibility but ignored.
 */
async function loadEnglishStrings(): Promise<LangStrings> {
  return (await import('../i18n/en')).default;
}

export async function generateProductPdf(product: Product, _strings: LangStrings, companyName?: string, companyWebsite?: string): Promise<void> {
  void _strings;
  const strings = await loadEnglishStrings();
  const p = strings.pdf;
  const logo = await loadLogo();
  const watermark = await loadWatermark();
  const logoMap = await loadProductLogos([product]);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  generateProductPage(doc, product, true, logo, watermark, logoMap, p, companyName?.toUpperCase() ?? 'VIVATIS PHARMA GmbH', companyWebsite ?? 'www.vivatis.de');
  addPageNumbers(doc);
  const fileName = `Vivatis_${product.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  downloadBlob(doc, fileName);
}

async function buildDocumentPdf(
  documentProducts: Product[],
  documentTitle?: string,
  _strings?: LangStrings,
  contactPerson?: string,
  companyName?: string,
  companyWebsite?: string,
): Promise<{ doc: jsPDF; fileName: string }> {
  void _strings;
  const p = (await loadEnglishStrings()).pdf;

  const logo = await loadLogo();
  const watermark = await loadWatermark();
  const logoMap = await loadProductLogos(documentProducts);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // --- Cover page ---
  const bw = 3.5;
  const margin = 16;
  doc.setFillColor(...VIVATIS_ORANGE);
  doc.rect(0, 0, pageW, pageH, 'F');
  doc.setFillColor(...WHITE);
  doc.rect(bw, bw, pageW - bw * 2, pageH - bw * 2, 'F');

  const cy = (pageH - 100) / 2 - 20;
  addLogo(doc, logo, pageW / 2 - 25, cy + 8, 50);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(26, 31, 36);
  doc.text(p.productDataSheets, pageW / 2, cy + 50, { align: 'center' });

  if (documentTitle) {
    doc.setFontSize(11);
    doc.setTextColor(...META_GRAY);
    doc.text(sanitize(documentTitle), pageW / 2, cy + 62, { align: 'center' });
  }

  doc.setFontSize(8.5);
  doc.setTextColor(...LABEL_GRAY);
  const countStr = documentProducts.length === 1
    ? p.productCountOne.replace(':count', '1')
    : p.productCountOther.replace(':count', String(documentProducts.length));
  doc.text(countStr, pageW / 2, cy + 74, { align: 'center' });

  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(dateStr, pageW / 2, cy + 81, { align: 'center' });

  if (contactPerson) {
    const cpY = cy + 90;
    doc.setDrawColor(...VIVATIS_ORANGE);
    doc.setLineWidth(0.4);
    doc.line(pageW / 2 - 18, cpY, pageW / 2 + 18, cpY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...LABEL_GRAY);
    doc.text(p.contactPerson.toUpperCase(), pageW / 2, cpY + 5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...ANTHRACITE);
    doc.text(sanitize(contactPerson), pageW / 2, cpY + 12, { align: 'center' });
  }

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.setTextColor(...CONF_GRAY);
  const coverConfText = sanitize(p.confidential).toUpperCase();
  doc.text(coverConfText, (pageW - doc.getTextWidth(coverConfText)) / 2, pageH - 10);

  doc.setFillColor(...VIVATIS_ORANGE);
  doc.rect(0, pageH - 7, pageW, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(...WHITE);
  const resolvedCompanyName = companyName?.toUpperCase() ?? 'VIVATIS PHARMA GmbH';
  const resolvedCompanyWebsite = companyWebsite ?? 'www.vivatis.de';
  doc.text(resolvedCompanyName, margin, pageH - 2.5);
  doc.setFont('helvetica', 'normal');
  doc.text(resolvedCompanyWebsite, pageW - margin, pageH - 2.5, { align: 'right' });

  // --- Product pages ---
  for (const product of documentProducts) {
    generateProductPage(doc, product, false, logo, watermark, logoMap, p, resolvedCompanyName, resolvedCompanyWebsite);
  }

  addPageNumbers(doc);

  const fileName = documentTitle
    ? `Vivatis_${documentTitle.replace(/\s+/g, '_')}.pdf`
    : 'Vivatis_Product_Document.pdf';

  return { doc, fileName };
}

export async function generateDocumentPdf(
  documentProducts: Product[],
  documentTitle?: string,
  strings?: LangStrings,
  contactPerson?: string,
  companyName?: string,
  companyWebsite?: string,
): Promise<void> {
  if (documentProducts.length === 0) return;
  const { doc, fileName } = await buildDocumentPdf(documentProducts, documentTitle, strings, contactPerson, companyName, companyWebsite);
  downloadBlob(doc, fileName);
}

export async function generateDocumentPdfBlob(
  documentProducts: Product[],
  documentTitle?: string,
  strings?: LangStrings,
  contactPerson?: string,
  companyName?: string,
  companyWebsite?: string,
): Promise<{ blob: Blob; fileName: string }> {
  if (documentProducts.length === 0) {
    throw new Error('No products to generate PDF for');
  }
  const { doc, fileName } = await buildDocumentPdf(documentProducts, documentTitle, strings, contactPerson, companyName, companyWebsite);
  return { blob: doc.output('blob'), fileName };
}
